import "../../../i18n/config";
import React, { useCallback, useEffect } from "react";
import styles from "./admin-notifications.module.scss";
import { useTranslation } from "react-i18next";
import { Switch } from "@whatslly/ui-components/v2/BaseElements/Switch";
import { useDependencyInjector } from "@whatslly/commons/di";
import { AdminNotificationsStore } from "../../stores/AdminNotificationsStore";
import { LOADER_IMG } from "@whatslly/commons/constants";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";
import { observer } from "mobx-react-lite";
import { MultipleInputSelect } from "@whatslly/ui-components/MultipleInputSelect";
import isEmail from "validator/es/lib/isEmail";
import { Button } from "@whatslly/ui-components/v2/Button";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";

export const AdminNotificationsSettingsPage: React.FC<{ hideTitle?: boolean }> = observer(({ hideTitle }) => {
  const adminNotificationsStore = useDependencyInjector(AdminNotificationsStore);
  const { t } = useTranslation();

  const saveSettings = useCallback(async () => {
    const res = await adminNotificationsStore.save();
    modalService.openModal(Toast, {
      text: res?.success ? t("adminNotifications.saved_successfully") : t("adminNotifications.saved_failed"),
    });
  }, [adminNotificationsStore, t]);

  useEffect(() => {
    adminNotificationsStore.setProps(t);
  }, [adminNotificationsStore, t]);

  return (
    <div className={styles.page}>
      {!hideTitle && (
        <div className={styles.header} data-testid="AdminNotifications">
          {t("adminNotifications.title")}
        </div>
      )}
      {adminNotificationsStore.init.case({
        pending: () => (
          <div className={styles.loading}>
            <img alt="tuvis loader" src={LOADER_IMG} />
            <div>{t("autoSyncActivitiesPage.loading")}</div>
          </div>
        ),
        fulfilled: () => (
          <div className={styles.containerWrapper}>
            <div className={styles.container}>
              <div className={styles.left}>
                <div className={styles.subHeader}>{t("adminNotifications.subHeader")}</div>
                {adminNotificationsStore.fullOptions?.map((option) => {
                  return (
                    <div key={option.label} className={styles.option}>
                      <Switch
                        testId={option.label.toLowerCase().replaceAll(" ", "_")}
                        scale={`small`}
                        paddingInline="none"
                        checked={option.val}
                        disabled={adminNotificationsStore.isReadonly}
                        onChange={(e) => adminNotificationsStore.onSwitchChange(e.target.checked, option.label)}
                      />
                      <span className={styles.optionLabel}>{option.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className={styles.right}>
                <div className={styles.rightSubHeader}>{t("adminNotifications.recipients")}</div>
                <div className={styles.subHeader}>{t("adminNotifications.recipients_text")}</div>
                <MultipleInputSelect
                  prefix={`emails`}
                  testId={`emails`}
                  items={adminNotificationsStore?.emails}
                  onChange={adminNotificationsStore.handleEmailInputChange}
                  isValidFn={isEmail}
                  itemType={`email`}
                  showError={true}
                  disabled={adminNotificationsStore.isReadonly}
                />
              </div>
            </div>
            {!adminNotificationsStore.isReadonly && (
              <div className={styles.footer}>
                <Button paddingInline="small" isSubmitting={adminNotificationsStore.isSaving} onClick={saveSettings} testId={`save-settings`}>
                  {t("adminNotifications.saveSettings")}
                </Button>
              </div>
            )}
          </div>
        ),
        rejected: (error: Error) => <CrmComponentsErrorBox error={error.message} />,
      })}
    </div>
  );
});
